import { FC, forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import cs from 'classnames';
import { useMethods } from '@lhb/hook';
import { floorKeep, isNotEmpty, replaceEmpty } from '@lhb/func';
import { formatNum, formatNumForTenThousand } from './index';
import { getModelClusterPopulation } from '@/common/api/networkplan';
import { XTCLabel } from '@/views/youth/pages/map/ts-config';
import styles from './index.module.less';
import { Modal } from 'antd';
import TagGroup from '@/common/components/TagGroup';
import StatisticsItem from './StatisticsItem';
import V2DetailGroup from '@/common/components/Feedback/V2DetailItem/Group';
import V2DetailItem from '@/common/components/Feedback/V2DetailItem';
import V2PieChart from '@/common/components/Charts/V2PieChart';

/**
 * 商场模块-商场信息详情弹窗
 * @description
 * @demo
 */
const Component:FC<{
  /** 商圈id */
  modelClusterId: number,
  /** 商圈名称 */
  clusterName: string;
  /** 商场信息 */
  placeData: any,
  ref?: any,
}> = forwardRef(({ modelClusterId, clusterName, placeData }, ref) => {

  useImperativeHandle(ref, () => ({
    init: methods.init,
  }));

  /* state */
  const [open, setOpen] = useState(false);
  const [clusterPopulation, setClusterPopulation] = useState<any>({});

  const tags = [
    { label: placeData.projectLevel, img: XTCLabel[placeData.projectLevel], imgWidth: '32.5px', imgHeight: '21px', color: 'orange' },
    { label: placeData.openStatus, color: 'blue' },
    { label: placeData.isitafranchise === '是' ? '连锁' : '', color: 'grey' },
    { label: placeData.projectGrade, color: 'grey' },
  ].filter(item => isNotEmpty(item.label));

  /** 孩子年龄映射表：{ '0-3岁': 1, '4-6岁': 1, '7-12岁': 1, '13-17岁': 1 } */
  const kidsagedshareMap = (() => {
    const data = clusterPopulation?.distributions?.find(item => item.type === '孩子年龄')?.parts;
    return (Array.isArray(data) ? data : []).reduce((prev, cur) => {
      return {
        ...prev,
        [cur?.name]: isNotEmpty(cur?.rate) ? cur.rate : null,
      };
    }, {});
  })();
  const statisticsData = useMemo(() => {
    const topData = [
      { label: '工作日均客流', value: isNotEmpty(placeData.weekdayDailyFlow) ? floorKeep(placeData.weekdayDailyFlow, 1, 3, 0) : '', unit: '人次' },
      { label: '节假日均客流', value: isNotEmpty(placeData.weekendDailyFlow) ? floorKeep(placeData.weekendDailyFlow, 1, 3, 0) : '', unit: '人次' },
      { label: '日均客流', value: isNotEmpty(placeData.dailyFlow) ? floorKeep(placeData.dailyFlow, 1, 3, 0) : '', unit: '人次' },
      { label: '节假日客流排名', value: placeData.weekendFlowRank, unit: '' },
      // 年日均客流（万人次）
      { label: '年日均客流', value: formatNumForTenThousand(placeData.annualTrafficAvgDaily)?.num, unit: `${formatNumForTenThousand(placeData.annualTrafficAvgDaily)?.unit || ''}人次` },
    ];
    const middleData = [
      // 处理单位：年销售额（万元）
      { label: '年销售额', value: formatNumForTenThousand(placeData.annualSales)?.num, unit: `${formatNumForTenThousand(placeData.annualSales)?.unit || ''}元` },
    ];
    const bottomData = [
      { label: '1km工作人口', value: isNotEmpty(placeData.workPopulation1km) ? floorKeep(placeData.workPopulation1km, 1, 3, 0) : '', unit: '人' },
      { label: '1km居住人口', value: isNotEmpty(placeData.livePopulation1km) ? floorKeep(placeData.livePopulation1km, 1, 3, 0) : '', unit: '人' },
      { label: '3km工作人口', value: isNotEmpty(placeData.workPopulation3km) ? floorKeep(placeData.workPopulation3km, 1, 3, 0) : '', unit: '人' },
      { label: '3km居住人口', value: isNotEmpty(placeData.livePopulation3km) ? floorKeep(placeData.livePopulation3km, 1, 3, 0) : '', unit: '人' },
      // 后端要求这么取值
      { label: '小孩4-6岁占比', value: kidsagedshareMap['4-6岁'], unit: '%' },
      { label: '小孩7-12岁占比', value: kidsagedshareMap['7-12岁'], unit: '%' },
    ];
    return process.env.CUSTOMIZED_PROJECT === 'xtc' ? [...topData, ...bottomData] : [...topData, ...middleData, ...bottomData];
  }, [placeData, kidsagedshareMap]);

  // 需要去掉接口自带的 %
  const businessMixData = useMemo(() => {
    const [retailratio, cateringformatproportion, entertainmentshare, kidFamilyShare, lifestyleServicesShare] = [
      placeData.retailratio, placeData.cateringformatproportion, placeData.entertainmentshare, placeData.kidFamilyShare, placeData.lifestyleServicesShare
    ].map(item => isNotEmpty(item) ? (+String(item).replace('%', '') || 0) : 0);
    const total = retailratio + cateringformatproportion + entertainmentshare + kidFamilyShare + lifestyleServicesShare;
    return [
      { name: '零售', value: floorKeep(retailratio / total * 100, 1, 3, 2) },
      { name: '餐饮', value: floorKeep(cateringformatproportion / total * 100, 1, 3, 2) },
      { name: '文娱', value: floorKeep(entertainmentshare / total * 100, 1, 3, 2) },
      { name: '亲子', value: floorKeep(kidFamilyShare / total * 100, 1, 3, 2) },
      { name: '生活服务', value: floorKeep(lifestyleServicesShare / total * 100, 1, 3, 2) },
    ];
  }, [placeData]);

  // 判断是否有业态占比数据
  const hasBusinessMixData = useMemo(() => {
    if (!Array.isArray(businessMixData) || businessMixData.length === 0) {
      return false;
    }
    // 检查是否有任何有效的非零值
    return businessMixData.some(item => {
      const value = item?.value;
      const numValue = typeof value === 'number' ? value : (typeof value === 'string' ? parseFloat(value) : 0);
      return !isNaN(numValue) && numValue > 0;
    });
  }, [businessMixData]);


  /** 商业面积，处理单位：商业面积（平方米） */
  const commercialVolume = (() => {
    const data = formatNum(placeData.commercialVolume);
    return isNotEmpty(data?.num) ? `${data.num || ''}${data.unit || ''}㎡` : '';
  })();

  /** 入驻品牌门店 */
  const competitorStoresTags = (Array.isArray(placeData.competitorStores) ? placeData.competitorStores : [])
    .filter(item => item.exists)
    .map(item => ({
      img: item.logo,
      imgHeight: '20px',
      label: item.name,
      tip: item.name,
    }));

  /* hooks */

  /* methods */
  const methods = useMethods({

    init() {
      setOpen(true);
      methods.getModelClusterPopulation();
    },

    /** 人口年龄性别人口性别年龄占比 */
    getModelClusterPopulation() {
      getModelClusterPopulation({ id: modelClusterId }).then((res) => {
        setClusterPopulation(res || {});
      });
    },
    /**
     * 根据字符串长度计算高度
     * @param {string} text - 要计算的字符串
     * @param {number} charsPerLine - 每行字符数，保险一行按20字算
     * @param {number} lineHeight - 每行高度(px)，默认 20
     * @returns {number} 高度(px)
     */
    calcTextHeight(text, charsPerLine = 20, lineHeight = 20) {
      if (!text) return floorKeep(lineHeight, 2, 3, 0);

      const length = text.length;
      const lines = Math.ceil(length / charsPerLine) + 1;// 添加一行，组件默认最后一行半透明，产品要求全部展示
      const height = floorKeep(lines, lineHeight, 3, 0);
      return Number(height) < 60 ? 60 : height;
    }
  });

  return (<Modal
    open={open}
    onCancel={() => setOpen(false)}
    width={910}
    title=' '
    centered
    footer={null}
    wrapClassName={styles.detailInfoModal}
  >
    <div className={styles.detailInfo}>
      {/* 头部信息 */}
      <div className={styles.header}>
        <div className={styles.title}>
          <span className={styles.text}>{replaceEmpty(clusterName)}</span>
          <TagGroup tags={tags} className={styles.tags} />
        </div>
      </div>
      <div className={styles.main}>
        {/* 入驻品牌门店 */}
        {Array.isArray(competitorStoresTags) && !!competitorStoresTags.length && <div className={styles.section}>
          <h2 className={styles.sectionTitle}>入驻品牌门店</h2>
          <div className={styles.sectionContent}>
            <TagGroup tags={competitorStoresTags} className={styles.competitorStores} />
          </div>
        </div>}

        {/* 统计数据 */}
        {Array.isArray(statisticsData) && !!statisticsData.length && <div className={styles.statisticsSection}>
          {statisticsData.map((item, index) => (
            <StatisticsItem key={index} {...item} className={styles.item} />
          ))}
        </div>}

        <div className={styles.infoWrapper}>
          {/* 业态占比 */}
          {hasBusinessMixData && (
            <div className={cs(styles.left, styles.businessProportion)}>
              <h2 className={styles.sectionTitle}>业态占比</h2>
              <div className={styles.chartWrapper}>
                <V2PieChart
                  type='circle'
                  seriesData={[{
                    data: Array.isArray(businessMixData) ? businessMixData : [],
                    unit: '%',
                    radius: ['70%', '90%'],
                  }]}
                  height={160}
                  wrapperClassName={styles.businessMixChart}
                />
              </div>
            </div>
          )}
          {/* 基础信息 */}
          <div className={cs(styles.right, styles.basicInfo, !hasBusinessMixData && styles.fullWidth)}>
            <h2 className={styles.sectionTitle}>基础信息</h2>
            <V2DetailGroup labelLength={4} direction='horizontal' className={cs(styles.basicInfoGroup, !hasBusinessMixData && styles.fourColumns)}>
              {isNotEmpty(commercialVolume) && <V2DetailItem label='商业面积' value={commercialVolume} className={styles.item} />}
              {isNotEmpty(placeData.openingTime) && <V2DetailItem label='开业时间' value={placeData.openingTime} className={styles.item} />}
              {isNotEmpty(placeData.cityLevel) && <V2DetailItem label='城市等级' value={placeData.cityLevel} className={styles.item} />}
              {isNotEmpty(placeData.province) && <V2DetailItem label='所在省' value={placeData.province} className={styles.item} />}
              {isNotEmpty(placeData.city) && <V2DetailItem label='所在市' value={placeData.city} className={styles.item} />}
              {isNotEmpty(placeData.district) && <V2DetailItem label='所在区' value={placeData.district} className={styles.item} />}
              {isNotEmpty(placeData.region) && <V2DetailItem label='业务地区' value={placeData.region} className={styles.item} />}
              {isNotEmpty(placeData.lightAsset) && <V2DetailItem label='轻资产' value={placeData.lightAsset} className={styles.item} />}
              {isNotEmpty(placeData.projectIndustry) && <V2DetailItem label='商场业态' value={placeData.projectIndustry} className={styles.item} />}
              {isNotEmpty(placeData.brandPosition) && <V2DetailItem label='商场定位' value={placeData.brandPosition} className={styles.item} />}
              {isNotEmpty(placeData.consumerAgeGroup) && <V2DetailItem label='消费年龄' value={placeData.consumerAgeGroup} className={styles.item} />}
              {isNotEmpty(placeData.consumerLevel) && <V2DetailItem label='消费档次' value={placeData.consumerLevel} className={styles.item} />}
              {isNotEmpty(placeData.groundFloor) && <V2DetailItem label='最低楼层' value={placeData.groundFloor} className={styles.item} />}
              {isNotEmpty(placeData.topFloor) && <V2DetailItem label='最高楼层' value={placeData.topFloor} className={styles.item} />}
              {isNotEmpty(placeData.propertyBrand) && <V2DetailItem label='项目品牌' value={placeData.propertyBrand} className={styles.item} />}
              {isNotEmpty(placeData.businessCircle) && <V2DetailItem label='商圈名称' value={placeData.businessCircle} className={styles.item} />}
              {isNotEmpty(placeData.address) && <V2DetailItem label='商场地址' value={[placeData.province, placeData.city, placeData.district, placeData.address].filter(Boolean).join('')} className={cs(styles.item, styles.fullWidth)} />}
              {isNotEmpty(placeData.developer) && <V2DetailItem label='开发商' value={placeData.developer} className={cs(styles.item, styles.fullWidth)} />}
              {isNotEmpty(placeData.managerName) && <V2DetailItem label='运营商' value={placeData.managerName} className={cs(styles.item, styles.fullWidth)} />}
              {isNotEmpty(placeData.placeDescription) && <V2DetailItem labelLength={5} label='项目简介' type='domExpand' value={placeData.placeDescription} domExpandHeight={methods.calcTextHeight(placeData.placeDescription)} className={cs(styles.item, styles.fullWidth)} />}
              {isNotEmpty(placeData.openedProjects) && <V2DetailItem labelLength={8} label='3km拟开项目' value={placeData.openedProjects} domExpandHeight={methods.calcTextHeight(placeData.openedProjects)} type='domExpand' className={cs(styles.item, styles.fullWidth)} />}
            </V2DetailGroup>
          </div>
        </div>
      </div>

    </div>
  </Modal>);
});

export default Component;
